import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useCart } from "@/lib/cart";
import { aud } from "@/lib/catalog";
import { api, formatApiError } from "@/lib/api";
import { StateSelect } from "./StateSelect";
import { OrderConfirmation } from "./OrderConfirmation";

const FIELDS = [
  ["name", "Full name", "text", "sm:col-span-2"],
  ["email", "Email", "email", ""],
  ["phone", "Phone", "tel", ""],
  ["address", "Street address", "text", "sm:col-span-2"],
  ["suburb", "Suburb / town", "text", ""],
  ["postcode", "Postcode", "text", ""],
];

export const Checkout = () => {
  const c = useCart();
  const [f, setF] = useState({ name: "", email: "", phone: "", address: "", suburb: "", postcode: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [order, setOrder] = useState(null);

  const close = (v) => {
    c.setCheckout(v);
    if (!v) setTimeout(() => setOrder(null), 300);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!c.state) return setError("Please choose your state or territory.");
    setBusy(true);
    setError("");
    try {
      const { notes, ...customer } = f;
      const { data } = await api.post("/orders", {
        items: c.lines.map((l) => ({ product_id: l.id, qty: l.qty, colour: l.variant || undefined })),
        customer: { ...customer, state: c.state },
        notes,
      });
      setOrder(data);
      c.clear();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={c.checkout} onOpenChange={close}>
      <DialogContent data-testid="checkout-modal-container" data-lenis-prevent className="max-h-[92vh] max-w-2xl overflow-y-auto rounded-[28px] border-0 bg-[#F5F0E8] p-6 sm:p-10">
        {order ? (
          <OrderConfirmation order={order} />
        ) : (
          <>
            <DialogHeader className="text-left">
              <DialogTitle className="font-display text-4xl font-black uppercase text-[#112E23] sm:text-5xl">Checkout</DialogTitle>
              <DialogDescription className="text-[#112E23]/75">Enter your delivery details. You'll pay by bank transfer after placing your order.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="mt-4 space-y-6">
              <div className="grid gap-4 sm:grid-cols-2">
                {FIELDS.map(([k, label, type, span]) => (
                  <label key={k} className={`block ${span}`}>
                    <span className="mb-1.5 block text-sm font-semibold text-[#112E23]">{label}</span>
                    <Input data-testid={`checkout-${k}-input`} required type={type} value={f[k]} onChange={(e) => { setF({ ...f, [k]: e.target.value }); if (k === "postcode") c.setPostcode(e.target.value.replace(/\D/g, "").slice(0, 4)); }} className="h-12 rounded-xl border-[#112E23]/20 bg-white" />
                  </label>
                ))}
                <label className="block sm:col-span-2">
                  <span className="mb-1.5 block text-sm font-semibold text-[#112E23]">State / territory</span>
                  <StateSelect value={c.state} onChange={c.setState} testId="checkout-state-select" className="border-[#112E23]/20 bg-white" />
                </label>
                <label className="block sm:col-span-2">
                  <span className="mb-1.5 block text-sm font-semibold text-[#112E23]">Notes (optional)</span>
                  <Textarea data-testid="checkout-notes-input" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="rounded-xl border-[#112E23]/20 bg-white" />
                </label>
              </div>

              <div data-testid="checkout-summary" className="rounded-2xl bg-[#112E23] p-5 font-mono text-sm text-[#F5F0E8]">
                {c.lines.map((l) => (
                  <div key={l.id} className="flex justify-between py-0.5"><span>{l.qty} × {l.name}</span><span>{aud(l.total)}</span></div>
                ))}
                <div className="mt-2 flex justify-between border-t border-[#F5F0E8]/15 pt-2"><span>Postage</span><span>{c.state ? aud(c.postage) : "—"}</span></div>
                <div className="flex justify-between pt-1 text-base font-semibold text-[#D99B26]"><span>Total AUD</span><span data-testid="checkout-total">{aud(c.total)}</span></div>
              </div>

              {error && <p data-testid="checkout-error" className="rounded-xl bg-[#FF4D30]/10 px-4 py-3 text-sm text-[#a3260f]">{error}</p>}
              <button data-testid="checkout-submit-order-button" disabled={busy || !c.count} className="btn-dark w-full justify-center disabled:opacity-50">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Place order
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
